import { useContext } from 'react';

import { CheckoutContext } from '@/context/CheckoutContext';
import { FeatureFlag } from '@/constants/featureFlags';

/**
 * ACDEV-25067 + ACDEV-27914 UI gate: is the wallet-derived-ZIP + tax
 * pipeline active for this session? True iff EITHER
 *   - `WALLET_ZIP_TAX` feature flag is ON (ACDEV-25067 global opt-in), OR
 *   - `sessionStartedWithoutValidTax` was snapshotted true at boot
 *     (ACDEV-27914 per-session fallback — US/CA buyer hydrated without a
 *     resolved tax).
 *
 * Consumed by the wallet forms (`{Apple,Google}Pay{,Adyen,Stripe}Form`) and
 * `ExpressCheckoutRenderer` to decide whether to hide the on-page ZIP input
 * and drop the pre-branch ZIP-blocker overlay. This is DELIBERATELY the raw
 * composite — NOT the US/CA-scoped `shouldUseWalletDerivedZipTax` — because
 * the pre-branch call sites already had the shape `FF || snapshot` (they let
 * downstream utils/`InputTaxComponent` apply the US/CA scope) and the UI
 * effect of the two forms is identical for non-US/CA sessions (the on-page
 * `InputTaxComponent` renders null there anyway).
 *
 * If you need the behavior gate (e.g. request wallet shipping address, wire
 * `onShippingAddressChange`), use `shouldUseWalletDerivedZipTax` — it adds
 * the US/CA scope so non-US/CA wallets stay shipping-free.
 */
export const useIsWalletZipTaxActive = (): boolean => {
  const { featureFlags, sessionStartedWithoutValidTax } = useContext(CheckoutContext);
  return featureFlags[FeatureFlag.WALLET_ZIP_TAX] || sessionStartedWithoutValidTax;
};
